import { supabase } from './supabase';
import { SoldProduct, WarrantyClaim, SoldProductSupply } from '../types';

export const warrantyService = {
  async getSoldProducts(filters?: {
    serialNumber?: string;
    customerName?: string;
    productType?: 'manufactured' | 'supply';
    warrantyStatus?: 'active' | 'expired' | 'all';
    startDate?: string;
    endDate?: string;
  }): Promise<SoldProduct[]> {
    let query = supabase
      .from('sold_products')
      .select(`
        *,
        manufactured_product:manufactured_products(*),
        supply:supplies(*),
        supplies_used:sold_product_supplies(*),
        warranty_claims(*)
      `)
      .order('sale_date', { ascending: false });

    if (filters?.serialNumber) {
      query = query.ilike('serial_number', `%${filters.serialNumber}%`);
    }

    if (filters?.customerName) {
      query = query.ilike('customer_name', `%${filters.customerName}%`);
    }

    if (filters?.productType) {
      query = query.eq('product_type', filters.productType);
    }

    if (filters?.startDate) {
      query = query.gte('sale_date', filters.startDate);
    }

    if (filters?.endDate) {
      query = query.lte('sale_date', filters.endDate);
    }

    const { data, error } = await query;

    if (error) throw error;

    let products = data || [];

    if (filters?.warrantyStatus && filters.warrantyStatus !== 'all') {
      const now = new Date();
      products = products.filter(product => {
        const warrantyEnd = new Date(product.warranty_end_date);
        const isExpired = warrantyEnd < now;
        return filters.warrantyStatus === 'expired' ? isExpired : !isExpired;
      });
    }

    return products;
  },

  async getSoldProductById(id: string): Promise<SoldProduct | null> {
    const { data, error } = await supabase
      .from('sold_products')
      .select(`
        *,
        manufactured_product:manufactured_products(*),
        supply:supplies(*),
        supplies_used:sold_product_supplies(*),
        warranty_claims(*)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getSoldProductBySerial(serialNumber: string): Promise<SoldProduct | null> {
    const { data, error } = await supabase
      .from('sold_products')
      .select(`
        *,
        manufactured_product:manufactured_products(*),
        supply:supplies(*),
        supplies_used:sold_product_supplies(*),
        warranty_claims(*)
      `)
      .eq('serial_number', serialNumber)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async createSoldProduct(soldProduct: Partial<SoldProduct>): Promise<SoldProduct> {
    const { data, error } = await supabase
      .from('sold_products')
      .insert(soldProduct)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateSoldProduct(id: string, updates: Partial<SoldProduct>): Promise<SoldProduct> {
    const { data, error } = await supabase
      .from('sold_products')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteSoldProduct(id: string): Promise<void> {
    const { error } = await supabase
      .from('sold_products')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getWarrantyClaims(soldProductId?: string): Promise<WarrantyClaim[]> {
    let query = supabase
      .from('warranty_claims')
      .select(`
        *,
        sold_product:sold_products(*)
      `)
      .order('claim_date', { ascending: false });

    if (soldProductId) {
      query = query.eq('sold_product_id', soldProductId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async getWarrantyClaimById(id: string): Promise<WarrantyClaim | null> {
    const { data, error } = await supabase
      .from('warranty_claims')
      .select(`
        *,
        sold_product:sold_products(*)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async createWarrantyClaim(claim: Partial<WarrantyClaim>): Promise<WarrantyClaim> {
    const { data: claimNumber } = await supabase.rpc('generate_claim_number');

    const { data, error } = await supabase
      .from('warranty_claims')
      .insert({
        ...claim,
        claim_number: claimNumber,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateWarrantyClaim(id: string, updates: Partial<WarrantyClaim>): Promise<WarrantyClaim> {
    const { data, error } = await supabase
      .from('warranty_claims')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteWarrantyClaim(id: string): Promise<void> {
    const { error } = await supabase
      .from('warranty_claims')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getSoldProductSupplies(soldProductId: string): Promise<SoldProductSupply[]> {
    const { data, error } = await supabase
      .from('sold_product_supplies')
      .select('*')
      .eq('sold_product_id', soldProductId)
      .order('supply_name', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getWarrantyStats(): Promise<{
    totalActive: number;
    totalExpired: number;
    totalClaims: number;
    pendingClaims: number;
  }> {
    const now = new Date().toISOString();

    const [productsResult, claimsResult] = await Promise.all([
      supabase.from('sold_products').select('warranty_end_date', { count: 'exact' }),
      supabase.from('warranty_claims').select('status', { count: 'exact' }),
    ]);

    if (productsResult.error) throw productsResult.error;
    if (claimsResult.error) throw claimsResult.error;

    const allProducts = productsResult.data || [];
    const totalActive = allProducts.filter(p => new Date(p.warranty_end_date) >= new Date()).length;
    const totalExpired = allProducts.length - totalActive;

    const allClaims = claimsResult.data || [];
    const pendingClaims = allClaims.filter(c => c.status === 'pending').length;

    return {
      totalActive,
      totalExpired,
      totalClaims: allClaims.length,
      pendingClaims,
    };
  },
};
